import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Alert, Box, Chip, Link, Paper, Stack, Typography } from '@mui/material';
import PublicContentPageShell from '@components/layout/PublicContentPageShell';
import { brandColors } from '@config/brandTokens';
import {
  ENCYCGAMES_BLOGS,
  ENCYCGAMES_BLOGS_PLACEHOLDER,
  getBlogBySlug,
} from '@/content/encycGamesBlogs';

const BlogsPage: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const post = slug ? getBlogBySlug(slug) : null;

  if (slug && !post) {
    return (
      <PublicContentPageShell title="Blogs" subtitle="EncycGames launch notes.">
        <Typography sx={{ mb: 1 }}>Post not found.</Typography>
        <Link component={RouterLink} to="/blogs" underline="hover">
          Back to blogs
        </Link>
      </PublicContentPageShell>
    );
  }

  if (post) {
    return (
      <PublicContentPageShell title={post.title} subtitle={`${post.date} · EncycGames`}>
        {ENCYCGAMES_BLOGS_PLACEHOLDER ? (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Placeholder article — replace copy in <code>frontend/src/content/encycGamesBlogs.ts</code>.
          </Alert>
        ) : null}
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          {post.tags.map((tag) => (
            <Chip key={tag} size="small" label={tag} sx={{ fontWeight: 600 }} />
          ))}
        </Stack>
        <Paper
          elevation={0}
          sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: { xs: 2, md: 2.5 }, bgcolor: brandColors.white }}
        >
          {post.body.map((para, idx) => (
            <Typography
              key={`${post.slug}-${idx}`}
              sx={{ color: brandColors.slate, lineHeight: 1.7, mb: 1.5, fontSize: '0.98rem' }}
            >
              {para}
            </Typography>
          ))}
        </Paper>
        <Box sx={{ mt: 2 }}>
          <Link component={RouterLink} to="/blogs" underline="hover" sx={{ fontWeight: 600 }}>
            ← All blogs
          </Link>
        </Box>
      </PublicContentPageShell>
    );
  }

  return (
    <PublicContentPageShell
      title="Blogs"
      subtitle="EncycGames launch posts. Titles and body text below are placeholders until final editorial copy arrives."
    >
      {ENCYCGAMES_BLOGS_PLACEHOLDER ? (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          5 sample cards — replace <code>frontend/src/content/encycGamesBlogs.ts</code> when ready. Routes
          <code> /blogs</code> and <code>/blogs/:slug</code> stay the same.
        </Alert>
      ) : null}
      <Stack spacing={1.5}>
        {ENCYCGAMES_BLOGS.map((item) => (
          <Paper
            key={item.slug}
            elevation={0}
            component={RouterLink}
            to={`/blogs/${item.slug}`}
            sx={{
              display: 'block',
              textDecoration: 'none',
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              p: { xs: 1.75, md: 2 },
              bgcolor: brandColors.white,
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              '&:hover': {
                borderColor: brandColors.blue,
                boxShadow: '0 4px 16px rgba(17,23,57,0.06)',
              },
            }}
          >
            <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 0.5 }}>{item.title}</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: brandColors.slate, mb: 0.75 }}>{item.date}</Typography>
            <Typography sx={{ color: brandColors.slate, fontSize: '0.92rem', lineHeight: 1.5 }}>
              {item.excerpt}
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {item.tags.map((tag) => (
                <Chip key={tag} size="small" label={tag} variant="outlined" sx={{ fontWeight: 600 }} />
              ))}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </PublicContentPageShell>
  );
};

export default BlogsPage;
